
import moment, { Moment } from 'moment';
import { Autocomplete, Box } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import styles from "../../assets/styles/form.module.css";
import { useCallback, useEffect, useState } from "react";
import { PinnacleTextField } from "../../components/PinnacleTextField";
import { handleApiError } from "../../utils/errorHandler";
import { E_REQUIRED_BIRTHDATE, E_REQUIRED_FIRST_NAME, E_REQUIRED_LAST_NAME } from "../../utils/constants";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { profile } from "../../api/services/user";
import { getSchools } from '../../api/services/school';
import { School } from '../../api/interfaces/School';
import { FieldSkeletonWrapper } from '../../components/skeleton/SkeletonAccountSetup/SkeletonAccountSetup';
import getTheme from '../../theme/theme';
import { PinnacleThemes } from '../../theme/types';
import { StyledPinnacleDatePicker } from '../../components/PinnacleDatePicker/PinnacleDatePicker.styled';

interface AccountProfilePageProps {
    setIsOpen: (isOpen: boolean) => void;
}
export default function AccountProfilePage(props: AccountProfilePageProps) {
    const theme = getTheme(PinnacleThemes.BASE_THEME);

    // Api response state
    const [schoolsOption, setSchoolsOption] = useState<null | School[]>(null);

    // Loader
    const [profileLoading, setProfileLoading] = useState(true);
    const [schoolsLoading, setSchoolsLoading] = useState(true);

    const [formDisabled, setFormDisabled] = useState<boolean>(true);

    // First Name
    const [firstName, setFirstName] = useState('');
    const [firstNameTouched, setFirstNameTouched] = useState(false);
    const isFirstNameValid = (firstName ?? '').trim().length > 0;

    // Last Name
    const [lastName, setLastName] = useState('');
    const [lastNameTouched, setLastNameTouched] = useState(false);
    const isLastNameValid = lastName.trim().length > 0;

    // Birthdate
    const [birthDate, setBirthDate] = useState<Moment | null>(null);
    const [birthDateTouched, setBirthDateTouched] = useState(false);
    const isBirthDateValid = !!birthDate;

    // Calculate max date: today minus 5 years
    const maxAllowedDate = moment().subtract(5, 'years');

    // Email
    const [email, setEmail] = useState<string>('');

    // School
    const [school, setSchool] = useState<School | string | null>(null);
    const [schoolTouched, setSchoolTouched] = useState(false);

    const fetchProfile = useCallback(async (schools?: School[]) => {
        setProfileLoading(true);
        try {
            const response = await profile();
            const user = response.data.user;

            let matchedSchool: School | string | null = null;
            if (user?.school) {
                if (schools) {
                    const found = schools.find(s => s.name === user.school);
                    matchedSchool = found ?? user.school;
                } else {
                    matchedSchool = user.school;
                }
            }
            setSchool(matchedSchool);

            setFirstName(user?.firstName ?? '');
            setLastName(user?.lastName ?? '');
            setBirthDate(user?.birthDate ? moment(user.birthDate) : null);
            setEmail(user?.email ?? '');
            // setYearLevel(user?.yearLevel ?? '');

        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setProfileLoading(false);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            setFormDisabled(true);
            try {
                const schoolsResp = await getSchools();
                setSchoolsOption(schoolsResp.data);

                await fetchProfile(schoolsResp.data);
            } catch (e) {
                console.error(e);
            } finally {
                setSchoolsLoading(false);
            }
        };
        init();
    }, [fetchProfile]);

    const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFirstName(e.target.value);
        if (!firstNameTouched) setFirstNameTouched(true);
    };

    const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLastName(e.target.value);
        if (!lastNameTouched) setLastNameTouched(true);
    };

    const handleBirthDateChange = (newValue: Moment | null) => {
        setBirthDate(newValue);
        if (!birthDateTouched) setBirthDateTouched(true);
    };

    const handleSchoolChange = (
        _: React.SyntheticEvent,
        newValue: School | null
    ) => {
        setSchool(newValue ?? null);
        if (!schoolTouched) setSchoolTouched(true);
    };

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <form noValidate>
                    <Box className={styles["form-panel-box"]}>

                        {/* FIRST NAME */}
                        <FieldSkeletonWrapper loading={profileLoading}>
                            <PinnacleTextField
                                error={firstNameTouched && !isFirstNameValid}
                                color={firstNameTouched && !isFirstNameValid ? 'error' : 'primary'}
                                helperText={firstNameTouched && !isFirstNameValid ? E_REQUIRED_FIRST_NAME : ''}
                                id="firstName"
                                label="First name"
                                name="firstName"
                                variant="outlined"
                                autoFocus
                                onChange={handleFirstNameChange}
                                onBlur={() => setFirstNameTouched(true)}
                                value={firstName}
                                autoComplete="first-name"
                                required
                                disabled={formDisabled}
                            />
                        </FieldSkeletonWrapper>

                        {/* LAST NAME */}
                        <FieldSkeletonWrapper loading={profileLoading}>
                            <PinnacleTextField
                                error={lastNameTouched && !isLastNameValid}
                                color={lastNameTouched && !isLastNameValid ? 'error' : 'primary'}
                                helperText={lastNameTouched && !isLastNameValid ? E_REQUIRED_LAST_NAME : ''}
                                id="lastName"
                                name="lastName"
                                label="Last name"
                                variant="outlined"
                                onChange={handleLastNameChange}
                                onBlur={() => setLastNameTouched(true)}
                                value={lastName}
                                autoComplete="last-name"
                                required
                                disabled={formDisabled}
                            />
                        </FieldSkeletonWrapper>

                        {/* BIRTHDATE */}
                        <FieldSkeletonWrapper loading={profileLoading}>
                            <StyledPinnacleDatePicker
                                label="Birth Date"
                                value={birthDate}
                                onChange={(newValue) => handleBirthDateChange(newValue)}
                                slots={{
                                    openPickerIcon: () => <CalendarMonthIcon sx={{ color: theme.palette.common.white }} />,
                                }}
                                slotProps={{
                                    textField: {
                                        variant: 'outlined',
                                        required: true,
                                        error: birthDateTouched && !isBirthDateValid,
                                        helperText: birthDateTouched && !isBirthDateValid ? E_REQUIRED_BIRTHDATE : '',
                                        onBlur: () => setBirthDateTouched(true),
                                        disabled: formDisabled,
                                        fullWidth: true
                                    },
                                }}
                                maxDate={maxAllowedDate}
                                disableFuture
                            />
                        </FieldSkeletonWrapper>

                        {/* EMAIL */}
                        <FieldSkeletonWrapper loading={profileLoading}>
                            <PinnacleTextField
                                id="email"
                                name="email"
                                label="Email address"
                                variant="outlined"
                                value={email}
                                disabled={true}
                            />
                        </FieldSkeletonWrapper>

                        {/* SCHOOL */}
                        <FieldSkeletonWrapper loading={schoolsLoading}>
                            <Autocomplete
                                freeSolo
                                options={schoolsOption ?? []}
                                getOptionLabel={(option) => {
                                    if (typeof option === "string") return option;
                                    return option.name ?? '';
                                }}
                                isOptionEqualToValue={(option, value) => {
                                    if (typeof value === "string") return option.name === value;
                                    return option._id === value._id;
                                }}
                                value={school}
                                onChange={(_, newValue) => {
                                    if (typeof newValue === "string") {
                                        setSchool(newValue);
                                    } else {
                                        setSchool(newValue ?? null);
                                    }
                                    if (!schoolTouched) setSchoolTouched(true);
                                }}
                                onInputChange={(_, newInputValue) => {
                                    setSchool(newInputValue); // ✅ critical for typing
                                }}
                                disabled={formDisabled}
                                renderInput={(params) => (
                                    <PinnacleTextField
                                        autoComplete="off"
                                        {...params}
                                        label="School"
                                        variant="outlined"
                                        name="school"
                                        type="text"
                                        onBlur={() => setSchoolTouched(true)}
                                    />
                                )}
                            />
                        </FieldSkeletonWrapper>
                    </Box>
                </form>
            </LocalizationProvider>
        </>
    );
}